import { useEffect } from 'react';
import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { getTodaysDate } from '@frontend/date';
import { SchemaInsuranceVerificationAdapterServiceV1 } from '@frontend/schema';
import { FormFieldActionTypes, useForm } from '@frontend/design-system';
import { useGetFormData } from '../hooks/use-get-form-data';
import { useInsuranceType } from '../providers/insurance-type-provider';
import { usePersonInfoStore } from '../providers/person-info-provider';
import { OTHER_SUBSCRIBER_SELECTION_VALUE, RelationshipEnum } from '../utils/constants';
import { prepareInsuranceDetails, preparePracticeTypeList } from '../utils/prepare-data-utils/prepare-insurance-data';
import {
  formatDateForDisplay,
  getInsurancePayerDetails,
  getPayerCombinedId,
  removeNonAlphaNumericCharacters,
  sanitizeText,
  validateNationalProviderId,
  validateNationalProviderTaxId,
} from '../utils/utils';
import {
  useGetVerifiedInsuranceDetailsShallowStore,
  VerifiedInsuranceDetailsByPersonIDType,
} from './use-get-verified-insurance-details-store';

const MAX_DATE = getTodaysDate('YYYY-MM-DD');

export const useInsuranceForm = () => {
  const { personInfo, locationId } = usePersonInfoStore();
  const { insuranceType } = useInsuranceType();
  const personId = personInfo?.PersonID as string;
  const isSecondaryInsurance = insuranceType === InsuranceType.INSURANCE_TYPE_SECONDARY;

  const { verifiedInsuranceDetails: verifiedInsuranceDetailsData, insuranceRequestDetails } =
    useGetVerifiedInsuranceDetailsShallowStore('verifiedInsuranceDetails', 'insuranceRequestDetails');

  const {
    isLoading,
    payerList,
    nationalProviderIdList,
    primSubscriberList,
    providerList,
    insuranceDetails,
    practiceTypeList,
    isWellnessVertical,
  } = useGetFormData();

  if (insuranceDetails) {
    //For secondary insurance there is no relationShip value in insuranceDetails response so setting it to self
    insuranceDetails.relationship = isSecondaryInsurance ? RelationshipEnum.Self : insuranceDetails?.relationship;
  }

  const practiceTypeListOptions = isWellnessVertical
    ? preparePracticeTypeList(practiceTypeList?.practiceTypes || {})
    : [];
  const defaultPracticeType = isWellnessVertical ? practiceTypeList?.defaultPracticeType || '' : '';
  const isPrimarySubscriberRequired =
    primSubscriberList.length > 1 &&
    !!insuranceDetails?.relationship &&
    insuranceDetails?.relationship !== 'se' &&
    !isSecondaryInsurance;
  //verifiedInsuranceDetails API is hit only when insuranceTab is opened if insurance form is opened from schedule pulse verifiedInsuranceDetails API will not hit  and verifiedInsuranceDetailsData will be empty so using data from insuranceDetails API as it contains data of primary insurance and only primary insurance status are only shown in schedule pulse when it is opened from tab it can use verifiedInsuranceDetails data
  const insuranceErrorDetails =
    (insuranceType === InsuranceType.INSURANCE_TYPE_PRIMARY && insuranceDetails?.errorDetails) ||
    verifiedInsuranceDetailsData?.errorDetails;

  const isValidUSZip = (zipCode: string) => {
    const zipCodeRegex = /^\d{5}(-\d{4})?$/;

    if (!zipCodeRegex.test(zipCode)) {
      return 'Please enter a valid zip code';
    }
    return '';
  };

  const form = useForm({
    fields: {
      payerId: { type: 'dropdown', required: true },
      groupName: { type: 'text' },
      groupNumber: { type: 'text' },
      patientMemberId: { type: 'text', required: true },
      patientFirstName: { type: 'text', required: true },
      patientLastName: { type: 'text', required: true },
      patientBirthDate: { type: 'datePicker', required: true, maxDate: MAX_DATE },
      patientZipCode: { type: 'text', validator: ({ value }) => isValidUSZip(value) },
      practiceType: { type: 'dropdown', required: isWellnessVertical },
      nationalProviderId: {
        type: 'text',
        required: true,
        validator: ({ value }) => validateNationalProviderId(value),
      },
      taxId: { type: 'text', validator: ({ value }) => validateNationalProviderTaxId(value), required: true },
      relationship: { type: 'dropdown', required: true, value: RelationshipEnum.Self },
      primarySubscriber: {
        type: 'dropdown',
        required: isPrimarySubscriberRequired,
      },
      subscriberFirstName: { type: 'text' },
      subscriberLastName: { type: 'text' },
      subscriberBirthDate: {
        type: 'datePicker',
        maxDate: MAX_DATE,
      },
      subscriberMemberId: { type: 'text' },
      subscriberZipCode: { type: 'text', validator: ({ value }) => isValidUSZip(value) },
    },
    fieldStateReducer: (state, action) => {
      const isUpdateAction = action.type === FormFieldActionTypes.Update;
      const isBlurAction = action.type === FormFieldActionTypes.Blur;
      const isFocusAction = action.type === FormFieldActionTypes.Focus;

      if (isUpdateAction && action.payload.name === 'nationalProviderId') {
        const taxId =
          providerList.find((provider) => provider.nationalProviderId === action.payload.value)?.taxId ?? '';
        if (taxId) {
          return {
            ...state,
            taxId: {
              ...state.taxId,
              value: taxId,
              error: validateNationalProviderTaxId(taxId),
            },
          };
        }
      }

      if (
        isUpdateAction &&
        (action.payload.name === 'taxId' ||
          action.payload.name === 'patientMemberId' ||
          action.payload.name === 'groupNumber')
      ) {
        return {
          ...state,
          [action.payload.name]: {
            ...state[action.payload.name],
            value: removeNonAlphaNumericCharacters(action.payload.value),
          },
        };
      }

      if (isUpdateAction || isBlurAction || isFocusAction) {
        const isShowPrimSubscriber =
          isUpdateAction && action.payload.name === 'relationship' && primSubscriberList.length > 1
            ? action.payload.value !== RelationshipEnum.Self
            : state.relationship.value !== RelationshipEnum.Self;

        return {
          ...state,
          primarySubscriber: {
            ...state.primarySubscriber,
            value: isShowPrimSubscriber ? state?.primarySubscriber?.value : '',
            required: isShowPrimSubscriber,
          },
          subscriberFirstName: {
            ...state.subscriberFirstName,
            value: isShowPrimSubscriber ? state.subscriberFirstName.value : '',
            required: isShowPrimSubscriber,
          },
          subscriberLastName: {
            ...state.subscriberLastName,
            value: isShowPrimSubscriber ? state.subscriberLastName.value : '',
            required: isShowPrimSubscriber,
          },
          subscriberBirthDate: {
            ...state.subscriberBirthDate,
            value: isShowPrimSubscriber ? state.subscriberBirthDate.value : '',
            required: isShowPrimSubscriber,
          },
          subscriberMemberId: {
            ...state.subscriberMemberId,
            value: isShowPrimSubscriber ? state.subscriberMemberId.value : '',
            required: isShowPrimSubscriber,
          },
        };
      }

      return state;
    },
  });

  const { data: subscriberInfo, isLoading: isLoadingSubscriberInfo } =
    InsuranceDetailQueries.useGetSubscriberByDependentsPersonID(personId, locationId ? locationId : '', insuranceType, {
      disabled:
        isLoading ||
        !form.values.relationship ||
        form.values.relationship === RelationshipEnum.Self ||
        isSecondaryInsurance,
    });

  // seed subscriber details into form when relationship is changed and when we have only one subscriber
  useEffect(() => {
    const hasRelationshipAsSelf = form.values.relationship === RelationshipEnum.Self;
    const hasPrimarySubscriberValue = !!form.values.primarySubscriber;

    const shouldSeedPrimarySubscriber =
      !!form.values.relationship && !hasRelationshipAsSelf && !hasPrimarySubscriberValue && !isSecondaryInsurance;

    if (shouldSeedPrimarySubscriber) {
      // find guardian primary subscriber from the list
      const primarySubscriberDetailsAsGuardian = primSubscriberList?.find((subscriber) => subscriber?.isGuardian);

      const firstPrimarySubscriberValue = primSubscriberList?.[0]?.value || '';

      // If there is only one primary subscriber in the list, seed that value into the form
      // else seed the guardian primary subscriber value if available or else seed the first primary subscriber value
      const primarySubscriberValue =
        primSubscriberList?.length === 1
          ? firstPrimarySubscriberValue
          : primarySubscriberDetailsAsGuardian?.value || firstPrimarySubscriberValue;

      form.seedValues({
        primarySubscriber: primarySubscriberValue,
      });
    }

    // seed other primary subscriber when there is no primary subscriber value in the form which will allow users to add new subscriber manually in the form
    const shouldSeedOtherPrimarySubscriber =
      !!form.values.relationship &&
      !hasRelationshipAsSelf &&
      !hasPrimarySubscriberValue &&
      primSubscriberList.length === 0;

    if (shouldSeedOtherPrimarySubscriber) {
      form.seedValues({
        primarySubscriber: OTHER_SUBSCRIBER_SELECTION_VALUE,
      });
    }
    focusOnFormField('subscriberFirstName');
  }, [form.values.relationship, primSubscriberList]);

  // validate form when relationship is changed and there is no primary subscriber value in the form
  useEffect(() => {
    const shouldValidateFormForSecondarySubscriber = !!form.values.relationship && !form.values.primarySubscriber;
    if (shouldValidateFormForSecondarySubscriber) {
      form.validate();
    }
  }, [form.values.relationship, form.values.primarySubscriber]);

  // seed insurance details into form
  useEffect(() => {
    if (isLoading) {
      return;
    }
    //insuranceDetails is the data from PMS
    const details = prepareInsuranceDetails(insuranceDetails ?? {}, payerList, insuranceType);

    const isSamePersonId = verifiedInsuranceDetailsData?.personId === personId;

    const verifiedInsuranceDetails = isSamePersonId
      ? verifiedInsuranceDetailsData
      : ({} as VerifiedInsuranceDetailsByPersonIDType);

    // Attach nationProviderId if we received it from the PMS
    let nationalProviderId = verifiedInsuranceDetails?.nationalProviderId || details.nationalProviderId;

    // Attach providerTaxId if we received it from the PMS
    let providerTaxId = verifiedInsuranceDetails?.providerTaxId || details.providerTaxId || '';

    // If we have nationalProviderId, the find the providerTaxId from providerList
    if (nationalProviderId && !providerTaxId) {
      providerTaxId = providerList.find((provider) => provider.nationalProviderId === nationalProviderId)?.taxId ?? '';
    }

    // If we have providerTaxId, then find the nationalProviderId from providerList
    if (!nationalProviderId && providerTaxId) {
      nationalProviderId = providerList.find((provider) => provider.taxId === providerTaxId)?.nationalProviderId ?? '';
    }

    // If we don't have nationalProviderId and providerTaxId, we can use the first provider in the list
    // Since the DXC insurance verification API can check eligibility with any NPI or taxID
    if (!nationalProviderId && !providerTaxId && providerList.length) {
      const providerData = providerList?.find((provider) => provider.nationalProviderId || provider.taxId);
      nationalProviderId = providerData?.nationalProviderId || '';
      providerTaxId = providerData?.taxId || '';
    }

    const payerDetails = getInsurancePayerDetails(
      sanitizeText(verifiedInsuranceDetails?.payerId || details?.payerId || ''),
      sanitizeText(verifiedInsuranceDetails?.payerName || details?.payerName || ''),
      payerList
    );

    const combinedPayerId = payerDetails?.payerName
      ? getPayerCombinedId(payerDetails?.payerID || details?.payerId || '', payerDetails.payerName)
      : '';

    const birthDate =
      details.dateOfBirth ||
      insuranceRequestDetails?.birthDate ||
      verifiedInsuranceDetails?.dateOfBirth ||
      personInfo?.Birthdate;

    form.seedValues({
      patientFirstName: verifiedInsuranceDetails?.firstName || personInfo?.FirstName || details.firstName,
      patientLastName: verifiedInsuranceDetails?.lastName || personInfo?.LastName || details.lastName,
      patientBirthDate: birthDate ? formatDateForDisplay(birthDate) : '',
      patientMemberId: verifiedInsuranceDetails?.memberId || details.memberId,
      groupName: verifiedInsuranceDetails?.groupName || details.groupName,
      groupNumber: verifiedInsuranceDetails?.groupNumber || details.groupNumber,
      payerId: combinedPayerId,
      relationship: verifiedInsuranceDetails?.relationship || details.relationship || RelationshipEnum.Self, //Default to Self
      nationalProviderId: nationalProviderId,
      taxId: providerTaxId,
      practiceType: defaultPracticeType,
      patientZipCode: details.zipCode || '',
    });

    const isProviderIdValid = validateNationalProviderId(nationalProviderId) === '';
    const isTaxIdValid = validateNationalProviderTaxId(providerTaxId) === '';
    if (isProviderIdValid && isTaxIdValid) {
      form.validate();
    }
  }, [
    isLoading,
    insuranceDetails,
    payerList,
    providerList,
    defaultPracticeType,
    personId,
    verifiedInsuranceDetailsData,
  ]);

  // seed subscriber details into form
  useEffect(() => {
    // SP-TODO: need to validate as it keeps failing currently and returns 404 error
    if (subscriberInfo) {
      form.seedValues({
        // SP-TODO: ask BE to update UUID type to string
        primarySubscriber: subscriberInfo.personId as string,
        subscriberFirstName: subscriberInfo.firstName,
        subscriberLastName: subscriberInfo.lastName,
        subscriberBirthDate: subscriberInfo?.birthDate ? formatDateForDisplay(subscriberInfo?.birthDate) : '',
        subscriberMemberId: sanitizeText(subscriberInfo.memberId || insuranceDetails?.memberId || '') || undefined,
        subscriberZipCode: subscriberInfo.zipCode || '',
      });
      focusOnFormField('subscriberMemberId');
    }
  }, [subscriberInfo, isLoadingSubscriberInfo, insuranceDetails?.memberId]);

  // seed subscriber details into form when primary subscriber is selected
  useEffect(() => {
    if (!form.values.primarySubscriber) {
      return;
    }

    // If user selects 'Other' as primary subscriber, clear the subscriber details
    if (form.values.primarySubscriber === OTHER_SUBSCRIBER_SELECTION_VALUE || isSecondaryInsurance) {
      form.seedValues({
        subscriberFirstName: '',
        subscriberLastName: '',
        subscriberBirthDate: '',
        subscriberMemberId: '',
      });
      focusOnFormField('subscriberFirstName');
      return;
    }

    const shouldGetSubscriberDetails =
      !form.values.subscriberFirstName ||
      form.values.subscriberFirstName !== subscriberInfo?.firstName ||
      !form.values.subscriberBirthDate ||
      form.values.subscriberBirthDate !== subscriberInfo?.birthDate;

    if (shouldGetSubscriberDetails) {
      SchemaInsuranceVerificationAdapterServiceV1.GetInsuranceByPersonID({
        personId: form.values.primarySubscriber,
      }).then((res) => {
        form.seedValues({
          subscriberFirstName: res.firstName,
          subscriberLastName: res.lastName,
          subscriberBirthDate: res?.birthDate ? formatDateForDisplay(res?.birthDate) : '',
          subscriberMemberId: sanitizeText(res.memberId || insuranceDetails?.memberId || ''),
        });
      });
      // Focus on subscriber member id field when primary subscriber is selected and validate the form
      focusOnFormField('subscriberMemberId');
    }

    form?.validate();
  }, [form.values.primarySubscriber, subscriberInfo, insuranceDetails?.memberId]);

  const focusOnFormField = (formFieldName: string) => {
    const formField = form?.formProps?.ref?.current?.querySelector(`input[name="${formFieldName}"]`) as HTMLElement;
    if (formField) {
      formField?.focus();
    }
  };

  return {
    form,
    isLoading,
    payerList,
    nationalProviderIdList,
    primSubscriberList,
    providerList,
    practiceTypeListOptions,
    isWellnessVertical,
    defaultPracticeType,
    insuranceErrorDetails,
  };
};
